import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  InjectionToken,
  Input,
} from '@angular/core';
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkFooterCell,
  CdkFooterCellDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
} from '@angular/cdk/table';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

/**
 * Cell definition for the iss-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
@Directive({
  selector: '[issCellDef]',
  providers: [ { provide: CdkCellDef, useExisting: CellDefDirective } ],
  standalone: true,
})
export class CellDefDirective extends CdkCellDef {}

/**
 * Header cell definition for the iss-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[issHeaderCellDef]',
  providers: [
    { provide: CdkHeaderCellDef, useExisting: HeaderCellDefDirective },
  ],
  standalone: true,
})
export class HeaderCellDefDirective extends CdkHeaderCellDef {}

/**
 * Footer cell definition for the iss-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
@Directive({
  selector: '[issFooterCellDef]',
  providers: [
    { provide: CdkFooterCellDef, useExisting: FooterCellDefDirective },
  ],
  standalone: true,
})
export class FooterCellDefDirective extends CdkFooterCellDef {}

export const ISS_SORT_HEADER_COLUMN_DEF = new InjectionToken<CdkColumnDef>(
  'ISS_SORT_HEADER_COLUMN_DEF',
);

/**
 * Column definition for the iss-table.
 * Defines a set of cells available for a table column.
 */
@Directive({
  selector: '[issColumnDef]',
  providers: [
    { provide: CdkColumnDef, useExisting: ColumnDefDirective },
    { provide: ISS_SORT_HEADER_COLUMN_DEF, useExisting: ColumnDefDirective },
  ],
  standalone: true,
})
export class ColumnDefDirective extends CdkColumnDef {
  /** Unique name for this column. */
  @Input('issColumnDef')
  public override get name(): string {
    return this._name;
  }

  public override set name(name: string) {
    this._setNameInput(name);
  }

  @Input() public override sticky = false;
}

/** Header cell template container that adds the right classes and role. */
@Directive({
  selector: 'th[issHeaderCell]',
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-header-cell',
    role: 'columnheader',
  },
  standalone: true,
})
export class HeaderCellDirective extends CdkHeaderCell {
  public constructor(columnDef: CdkColumnDef, elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(
      `iss-col-${ columnDef.cssClassFriendlyName }`,
    );
  }
}

/** Footer cell template container that adds the right classes and role. */
@Directive({
  selector: 'td[issFooterCell]',
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-footer-cell',
  },
  standalone: true,
})
export class FooterCellDirective extends CdkFooterCell {
  public constructor(columnDef: CdkColumnDef, elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(
      `iss-col-${ columnDef.cssClassFriendlyName }`,
    );
  }
}

/** Cell template container that adds the right classes and role. */
@Directive({
  selector: 'td[issCell]',
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-cell',
    role: 'gridcell',
  },
  standalone: true,
})
export class CellDirective extends CdkCell {
  public constructor(columnDef: CdkColumnDef, elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(
      `iss-col-${ columnDef.cssClassFriendlyName }`,
    );
  }
}

/** Expandable cell template container that adds the right classes and role. */
@Component({
  selector: 'td[issExpandableCell]', // eslint-disable-line @angular-eslint/component-selector
  host: { // eslint-disable-line @angular-eslint/no-host-metadata-property
    class: 'iss-expandable-cell',
    '[class.iss-expandable-cell--expanded]': 'expanded',
    role: 'gridcell',
    colspan: '999',
  },
  template: '<div style="overflow: hidden" [@expandCollapseHeight]="expanded"><ng-content/></div>',
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [
    trigger('expandCollapseHeight', [
      state('false', style({ height: '0', minHeight: '0' })),
      state('true', style({ height: '*' })),
      transition('true <=> false', animate('400ms ease')),
    ]),
  ],
  standalone: true,
})
export class ExpandableCellComponent extends CdkCell {
  public constructor(columnDef: CdkColumnDef, elementRef: ElementRef<HTMLElement>) {
    super(columnDef, elementRef);
    elementRef.nativeElement.classList.add(
      `iss-expandable-col-${ columnDef.cssClassFriendlyName }`,
    );
  }

  @Input('issExpandableCell')
  public expanded?: boolean;
}
